import { InteractionEvent } from '@logto/schemas';
import { useCallback, useContext } from 'react';

import UserInteractionContext from '@/Providers/UserInteractionContextProvider/UserInteractionContext';
import { updateProfileAndSubmit } from '@/apis/experience';
import useApi from '@/hooks/use-api';
import useErrorHandler from '@/hooks/use-error-handler';
import useGlobalRedirectTo from '@/hooks/use-global-redirect-to';
import useSubmitInteractionErrorHandler from '@/hooks/use-submit-interaction-error-handler';
import { type ContinueFlowInteractionEvent } from '@/types';
import { trackEvent, trackRegistrationCompleted, waitForMoEngageFlush } from '@/utils/analytics';
import { setSignupFromApp, identifyAndSyncUser } from '@/utils/moengage-user-attributes';
import { storePendingExtraProfile } from '@/utils/pending-extra-profile';

export const useSetExtraProfile = (
  interactionEvent: ContinueFlowInteractionEvent = InteractionEvent.Register,
  isSocialLogin = false,
  socialConnectorId?: string
) => {
  const asyncUpdateAndSubmit = useApi(updateProfileAndSubmit);
  const handleError = useErrorHandler();
  const redirectTo = useGlobalRedirectTo();
  const { identifierInputValue } = useContext(UserInteractionContext);

  // Pass linkSocial so the email/phone fulfillment page keeps the social context
  // (?link-social= powers both the bind-account modal and the MoEngage sync there)
  const submitErrorHandlers = useSubmitInteractionErrorHandler(interactionEvent, {
    linkSocial: socialConnectorId,
  });

  // For Register flows the user does NOT exist yet: identifier, password and extra
  // profile are staged in the interaction session by the previous steps, and
  // updateProfileAndSubmit creates the user + submits the interaction atomically here

  const onSubmit = useCallback(
    async (values: Record<string, unknown>) => {
      // Filter out 'name' only for social login to preserve social name (from Google, Facebook, etc.)
      // For phone/email registration, include the name from form input
      const valuesToSubmit = isSocialLogin
        ? (() => {
            const { name, ...rest } = values;
            return rest;
          })()
        : values;

      // Set signupFromApp attribute BEFORE API call to ensure it's always set for registrations
      let signUpFromApp: string | undefined;
      if (interactionEvent === InteractionEvent.Register) {
        signUpFromApp = await setSignupFromApp();
      }

      const [error, result] = await asyncUpdateAndSubmit(valuesToSubmit, interactionEvent);

      if (error) {
        if (interactionEvent === InteractionEvent.Register) {
          // Values are already staged server-side; keep a copy so the MoEngage sync
          // that runs after the email/phone verification step still receives them.
          storePendingExtraProfile(valuesToSubmit);
        }
        await handleError(error, submitErrorHandlers);
        return;
      }

      trackEvent(
        'extra_profile',
        'submitted',
        { eventId: 'registration_form_extra_profile_submitted' },
        { ...values }
      );

      // Sync all user attributes & fire registration event to MoEngage
      if (interactionEvent === InteractionEvent.Register) {
        // Build identifier from cached input (email/phone used to register)
        const identifier =
          identifierInputValue?.type === 'email' || identifierInputValue?.type === 'phone'
            ? { type: identifierInputValue.type, value: identifierInputValue.value }
            : undefined;

        const syncResult = await identifyAndSyncUser(values, identifier, socialConnectorId);
        trackRegistrationCompleted(signUpFromApp, syncResult?.userId, syncResult?.userAttributes);
        await waitForMoEngageFlush();
      }

      if (result?.redirectTo) {
        await redirectTo(result.redirectTo);
      }
    },
    [
      asyncUpdateAndSubmit,
      submitErrorHandlers,
      handleError,
      redirectTo,
      isSocialLogin,
      interactionEvent,
      identifierInputValue,
      socialConnectorId,
    ]
  );

  return onSubmit;
};
